/* Fixed native bundle snapshot contract. Refresh metadata after native component changes.
 * Shopify owns inventory and discounts. This controller never adds components or posts cart AJAX.
 */
(() => {
  if (customElements.get('bundle-product')) { return; }
  const readJSON = (root, selector) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || 'null'); }
    catch { return null; }
  };
  function verifiedContents(bundle, variant) {

    if (!bundle || !variant || bundle.version !== 1 || bundle.requires_components !== true ||
          String(bundle.parent_variant_id) !== String(variant.id) ||
          !/^\d+$/.test(String(bundle.parent_variant_id)) || !Array.isArray(bundle.components) ||
          ![2, 3, 4].includes(bundle.components.length) || ![12, 18, 24].includes(bundle.glass_count)) { return false; }
    const ids = new Set();
    let total = 0;
    for (const item of bundle.components) {
      if (!item || !/^\d+$/.test(String(item.variant_id)) || ids.has(String(item.variant_id)) ||
            item.quantity !== 1 || item.glasses !== 6 || typeof item.title !== 'string' || !item.title.trim()) { return false; }
      ids.add(String(item.variant_id));
      total += item.quantity * item.glasses;
    }
    return total === bundle.glass_count;
  }
  const specifications = {
    '47713258373269': { description: 'A generous Bordeaux-style bowl with room to swirl, finished with gently tapered sides.', capacity: '24 fl oz', height: '9.5 in', span: '3.7 in', spanLabel: 'Base diameter' },
    '47713258569877': { description: 'A narrower bowl and refined silhouette for white wine, with a broad base beneath the clear stem.', capacity: '18 fl oz', height: '9.25 in', span: '3.4 in', spanLabel: 'Base diameter' },
    '45671398113429': { description: 'A rounded tulip bowl that narrows toward the rim. A versatile shape for red, white, or rosé.', capacity: 'Approx. 16 US fl oz / 475 mL', height: '9.25 in', span: '3.7 in', spanLabel: 'Width' }
  };
  class BundleProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.attachRuntimeObservers(); return; }
      this.initialized = true;
      this.config = readJSON(this, '[data-bundle-config]');
      this.variant = readJSON(this, '[data-bundle-variant]');
      this.form = this.querySelector('form');
      this.main = this.form?.querySelector('[name="add"]');
      this.bar = this.querySelector('[data-bundle-context="true"]');
      this.sticky = this.bar?.querySelector('.floating-cart-bar__button');
      this.verified = verifiedContents(this.config, this.variant);
      const supported = this.verified && this.variant.requires_components !== false &&
        this.variant.requires_selling_plan !== true && !(this.variant.quantity_rule?.min > 1) &&
        !(this.variant.quantity_rule?.increment > 1) && this.dataset.bundlePurchaseBlocked !== 'true';
      this.purchasable = supported && this.variant.available === true;
      this.soldOut = supported && this.variant.available === false;
      this.notify = this.soldOut && window.saludiStoqConfig?.notifyEnabled !== false && typeof window.SaludiSoldOut?.openNotify === 'function';
      this.addEventListener('submit', event => {
        if (event.target !== this.form || !this.purchasable || !this.correctForm() || this.main.disabled || this.main.getAttribute('aria-disabled') === 'true') {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, true);
      if (this.main) {
        this.main.disabled = !(this.purchasable || this.notify);
        this.main.type = this.notify ? 'button' : 'submit';
        const label = this.querySelector('[data-bundle-submit-label]');
        if (label) { label.textContent = this.notify ? 'Notify me when available' : this.purchasable ? 'Add bundle to cart' : 'Unavailable'; }
        this.main.addEventListener('click', event => {
          if (this.notify) {
            event.preventDefault();
            if (this.correctForm() && this.main.isConnected && !this.main.disabled && this.main.getAttribute('aria-disabled') !== 'true') {
              window.SaludiSoldOut?.openNotify({ productId: this.dataset.productId, variantId: String(this.variant.id), sku: this.variant.sku || '', context: 'bundle', availabilityState: 'sold-out', mode: 'notify' });
            }
          }
        });
      }
      const status = this.querySelector('[data-bundle-status]');
      if (status) { status.hidden = this.purchasable; }
      if (this.verified) {
        const count = `${this.config.glass_count} glasses: ${this.config.components.map(item => `${item.glasses} ${item.title.trim()}`).join(' + ')}.`;
        this.querySelectorAll('[data-bundle-count]').forEach(node => { node.textContent = count; });
        this.querySelectorAll('[data-bundle-verified]').forEach(node => { node.hidden = false; });
        const stickyCount = this.querySelector('[data-bundle-sticky-count]');
        if (stickyCount) { stickyCount.textContent = `${this.config.glass_count} glasses · `; }
      }
      this.sticky?.addEventListener('click', event => {
        event.preventDefault();
        // Exact main CTA only. Never use a global selector or a direct cart fallback.
        if (this.canForward()) { this.main.click(); }
      });
      this.syncSticky = () => {
        if (!this.sticky) { return; }
        this.sticky.disabled = !this.canForward();
        this.sticky.textContent = this.main?.querySelector('[data-bundle-submit-label]')?.textContent || 'Unavailable';
      };
      this.syncSticky();
      if (this.main) {
        this.observer = new MutationObserver(this.syncSticky);

      }
      this.visibility = () => {
        if (!this.bar) { return; }
        const rect = this.main?.isConnected ? this.main.getBoundingClientRect() : null;
        const visible = Boolean(rect && rect.bottom <= 0 && window.scrollY > 100);
        if (!visible && this.bar.contains(document.activeElement) && this.main?.isConnected) {
          this.main.focus({ preventScroll: true });
        }
        this.bar.classList.toggle('is-visible', visible);
        this.bar.toggleAttribute('inert', !visible);
        this.bar.setAttribute('aria-hidden', String(!visible));
      };
      this.attachRuntimeObservers();

    }
    attachRuntimeObservers() {
      if (this.main && this.observer) {
        this.observer.observe(this.main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'type'], childList: true, characterData: true, subtree: true });
      }
      window.addEventListener('scroll', this.visibility, { passive: true });
      window.addEventListener('resize', this.visibility, { passive: true });
      this.syncSticky?.();
      this.visibility?.();
    }
    correctForm() {
      return Boolean(this.form?.isConnected && this.main?.isConnected && this.main.form === this.form &&
        this.form.elements.id?.value === String(this.config?.parent_variant_id) && this.form.elements.quantity?.value === '1');
    }
    canForward() {
      return Boolean(this.correctForm() && this.sticky?.dataset.targetButton === this.main.id &&
        (this.purchasable || this.notify) && !this.main.disabled && this.main.getAttribute('aria-disabled') !== 'true');
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.visibility);
      window.removeEventListener('resize', this.visibility);
    }
  }
  customElements.define('bundle-product', BundleProduct);
  class BundleContents extends HTMLElement {
    connectedCallback() {
      const config = readJSON(this, '[data-bundle-config]');
      const variant = readJSON(this, '[data-bundle-variant]');
      if (!verifiedContents(config, variant)) { return; }
      if (this.hasAttribute('data-bundle-static')) {
        this.querySelector('[data-bundle-verified]')?.removeAttribute('hidden');
        return;
      }
      const grid = this.querySelector('[data-bundle-specs]');
      if (!grid) { return; }
      const images = readJSON(this, '[data-bundle-shape-images]');
      const measurements = this.querySelector('[data-bundle-measurements]');
      const dimensions = this.querySelector('[data-bundle-dimensions]');
      grid.replaceChildren();
      measurements?.replaceChildren();
      config.components.forEach(item => {
        const article = document.createElement('article');
        article.className = 'bundle-shape-card';
        const source = Array.isArray(images) ? images.find(image => String(image.variant_id) === String(item.variant_id))?.url : null;
        if (typeof source === 'string' && /^(https:\/\/cdn\.shopify\.com\/|\/\/cdn\.shopify\.com\/|\/assets\/)/.test(source)) {
          const frame = document.createElement('div');
          frame.className = 'bundle-single-glass';
          frame.dataset.shapeVariant = String(item.variant_id);
          const image = document.createElement('img');
          image.src = source; image.alt = `One ${item.title.trim()} glass`; image.width = 640; image.height = 640; image.loading = 'lazy';
          frame.append(image); article.append(frame);
        }
        const title = document.createElement('h3'); title.textContent = item.title.trim(); article.append(title);
        const count = document.createElement('p'); count.className = 'bundle-shape-count'; count.textContent = `${item.glasses} included`; article.append(count);
        const spec = specifications[String(item.variant_id)];
        if (spec) {
          const description = document.createElement('p'); description.textContent = spec.description; article.append(description);
          if (measurements) {
            const row = document.createElement('tr');
            const heading = document.createElement('th'); heading.scope = 'row'; heading.textContent = item.title.trim(); row.append(heading);
            [spec.capacity, spec.height, `${spec.span} ${spec.spanLabel.toLowerCase()}`].forEach(value => {
              const cell = document.createElement('td'); cell.textContent = value; row.append(cell);
            });
            measurements.append(row);
          }
        }
        grid.append(article);
      });
      if (this.hasAttribute('data-transpose-dimensions') && measurements?.children.length) {
        const rows = Array.from(measurements.rows);
        const table = measurements.closest('table');
        const head = table.tHead || table.createTHead();
        head.replaceChildren();
        const headings = document.createElement('tr');
        ['Detail', ...rows.map(row => row.cells[0].textContent)].forEach(value => {
          const cell = document.createElement('th'); cell.scope = 'col'; cell.textContent = value; headings.append(cell);
        });
        head.append(headings);
        measurements.replaceChildren();
        ['Capacity', 'Height', 'Width / base'].forEach((label, index) => {
          const row = document.createElement('tr');
          const heading = document.createElement('th'); heading.scope = 'row'; heading.textContent = label; row.append(heading);
          rows.forEach(source => {
            const cell = document.createElement('td'); cell.textContent = source.cells[index + 1].textContent; row.append(cell);
          });
          measurements.append(row);
        });
      }
      if (dimensions) { dimensions.hidden = !measurements?.children.length; }
      this.querySelector('[data-bundle-verified]').hidden = false;
    }
  }
  customElements.define('bundle-contents', BundleContents);
})();
